import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { StrategyAdvisor, StrategyMode } from '../utils/strategyAdvisor';
import { Die, YatzyScores } from '../types/game';
import { mlFeedbackSystem } from '../utils/mlFeedbackSystem';

interface StrategyHelperProps {
  dice: Die[];
  scores: YatzyScores;
  rollsLeft: number;
  upperSectionScore: number;
  onSelectCategory?: (category: string) => void;
  onToggleDieLock?: (index: number) => void;
}

// Removed unused pulse animation

const HelperContainer = styled.div<{ $expanded: boolean }>`
  position: fixed;
  top: 20px;
  right: ${p => p.$expanded ? '20px' : '-320px'};
  width: 300px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95),
    rgba(240, 248, 255, 0.95)
  );
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 2px solid var(--theme-primary, #4CAF50);
  z-index: 1000;
  transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  
  @media (max-width: 768px) {
    width: calc(100vw - 40px);
    max-width: 320px;
    right: ${p => p.$expanded ? '20px' : 'calc(-100vw + 20px)'};
    top: 80px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const AdviceSection = styled.div`
  margin-bottom: 20px;
`;

const MainAdvice = styled.div`
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 10px;
`;

const AdviceText = styled.p`
  margin: 0 0 10px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
`;

const ConfidenceBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 10px;
`;

const ConfidenceFill = styled.div<{ $confidence: number }>`
  width: ${p => p.$confidence}%;
  height: 100%;
  background: ${p => 
    p.$confidence > 80 ? 'linear-gradient(90deg, #4caf50, #8bc34a)' :
    p.$confidence > 50 ? 'linear-gradient(90deg, #ff9800, #ffc107)' :
    'linear-gradient(90deg, #f44336, #ff9800)'
  };
  transition: width 0.5s ease;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, var(--theme-primary, #4CAF50), var(--theme-secondary, #45a049));
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const AlternativeSection = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border-radius: 10px;
  padding: 12px;
  margin-top: 10px;
  border: 1px solid rgba(255, 193, 7, 0.3);
`;

const DiceHint = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const DieIndicator = styled.div<{ $shouldKeep: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${p => p.$shouldKeep ? '#4caf50' : '#f44336'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  
  &::after {
    content: '${p => p.$shouldKeep ? '✓' : '↻'}';
    position: absolute;
    bottom: -2px;
    right: -2px;
    background: ${p => p.$shouldKeep ? '#2e7d32' : '#c62828'};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
  }
`;

const InfoText = styled.p`
  font-size: 12px;
  color: #666;
  margin: 10px 0 0 0;
  font-style: italic;
`;

const StrategyHelper: React.FC<StrategyHelperProps> = ({
  dice,
  scores,
  rollsLeft,
  upperSectionScore,
  onSelectCategory,
  onToggleDieLock
}) => {
  const [expanded, setExpanded] = useState(true); // Öppnad som standard när AI är aktiverad
  const [advice, setAdvice] = useState<ReturnType<typeof StrategyAdvisor.analyzePosition> | null>(null);
  const [strategyMode, setStrategyMode] = useState<StrategyMode>(StrategyMode.BALANCED);
  const [lastGameState, setLastGameState] = useState<any>(null);
  const [showExplain, setShowExplain] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<'up'|'down'|null>(null);
  // Button pulsing removed

  useEffect(() => {
    // Analysera position när något ändras
    if (dice.some(d => d.value > 0)) {
      const newAdvice = StrategyAdvisor.analyzePosition(dice, scores, rollsLeft, upperSectionScore, strategyMode);
      setAdvice(newAdvice);
      setFeedbackGiven(null);
      // Spara förenklad gameState för feedback referens
      setLastGameState({
        dice: dice.map(d=>d.value).sort((a,b)=>a-b),
        rollsLeft,
        scoreCard: scores,
        upperSectionSum: upperSectionScore,
        hasBonus: upperSectionScore >= 63,
        gamePhase: rollsLeft > 1 ? 'early' : rollsLeft > 0 ? 'mid' : 'late'
      });
    }
    // (Pulserande knapp borttagen)
  }, [dice, scores, rollsLeft, upperSectionScore, expanded, strategyMode]);

  const handleApplyAdvice = () => {
    if (!advice) return;
    
    if (advice.diceToKeep && onToggleDieLock) {
      // Lås rekommenderade tärningar
      dice.forEach((die, index) => {
        const shouldKeep = advice.diceToKeep?.includes(index);
        if (die.isLocked !== shouldKeep) {
          onToggleDieLock(index);
        }
      });
    } else if (advice.category && onSelectCategory) {
      // Välj rekommenderad kategori
      onSelectCategory(advice.category);
    }
    
    setExpanded(false);
  };

  const getDiceDisplay = () => {
    if (!advice?.diceToKeep) return null;
    
    return dice.map((die, index) => (
      <DieIndicator key={index} $shouldKeep={advice.diceToKeep?.includes(index) || false}>
        {die.value}
      </DieIndicator>
    ));
  };

  return (
    <>      
      <HelperContainer $expanded={expanded}>
        <Header>
          <Title>
            🤖 AI-Assistent
          </Title>
          <CloseButton onClick={() => setExpanded(false)}>
            ×
          </CloseButton>
        </Header>
        <div style={{display:'flex', gap:6, marginBottom:12}}>
          <select aria-label="Strategiläge" value={strategyMode} onChange={e=> setStrategyMode(e.target.value as unknown as StrategyMode)} style={{flex:1,padding:6,borderRadius:8,border:'1px solid #ccc'}}>
            <option value={StrategyMode.BALANCED}>⚖️ Balanserad</option>
            <option value={StrategyMode.AGGRESSIVE}>🔥 Aggressiv</option>
            <option value={StrategyMode.CONSERVATIVE}>🛡️ Säker</option>
          </select>
          <button aria-label="Förklaring" onClick={()=> setShowExplain(s=>!s)} style={{padding:'6px 10px',borderRadius:8,border:'1px solid #ccc',background: showExplain?'#d1eaff':'#f5f5f5',cursor:'pointer'}}>ℹ️</button>
        </div>
        
        {advice && (
          <>
            <AdviceSection>
              <MainAdvice>
                <AdviceText>
                  <strong>Rekommendation:</strong> {advice.reasoning}
                </AdviceText>
                
                {advice.category && (
                  <AdviceText>
                    <strong>Kategori:</strong> {advice.category}
                    {advice.expectedValue > 0 && ` (${Math.round(advice.expectedValue)} poäng)`}
                  </AdviceText>
                )}
                
                {advice.diceToKeep && (
                  <>
                    <AdviceText>
                      <strong>Behåll dessa tärningar:</strong>
                    </AdviceText>
                    <DiceHint>
                      {getDiceDisplay()}
                    </DiceHint>
                  </>
                )}
                
                <ConfidenceBar>
                  <ConfidenceFill $confidence={advice.confidence} />
                </ConfidenceBar>
                <InfoText>
                  Säkerhet: {advice.confidence}%
                </InfoText>
                {showExplain && (
                  <div style={{marginTop:10,fontSize:12,lineHeight:1.4,color:'#222'}}>
                    <strong>Varför detta?</strong><br/>
                    {advice.reasoning}<br/>
                    {advice.alternativeMove && (
                      <span style={{display:'block',marginTop:6}}>Alternativt: {advice.alternativeMove.reasoning}</span>
                    )}
                    {strategyMode===StrategyMode.AGGRESSIVE && <span style={{display:'block',marginTop:6}}>Aggressivt läge prioriterar högrisk-kategorier tidigare.</span>}
                    {strategyMode===StrategyMode.CONSERVATIVE && <span style={{display:'block',marginTop:6}}>Säkert läge föredrar bonusprogress och säkra poäng.</span>}
                  </div>
                )}
              </MainAdvice>
              
              {advice.alternativeMove && (
                <AlternativeSection>
                  <AdviceText>
                    <strong>Alternativ:</strong> {advice.alternativeMove.reasoning}
                  </AdviceText>
                </AlternativeSection>
              )}
            </AdviceSection>
            
            <ActionButton onClick={handleApplyAdvice}>
              {advice.diceToKeep ? '🎯 Lås rekommenderade tärningar' : '✅ Välj rekommenderad kategori'}
            </ActionButton>
            <div style={{display:'flex',justifyContent:'space-between',marginTop:12,gap:8}}>
              <button disabled={!advice||feedbackGiven==='up'} onClick={()=> { if(!advice||!lastGameState) return; mlFeedbackSystem.recordFeedback(lastGameState, advice.category ?? advice.reasoning, 'positive'); setFeedbackGiven('up'); }} style={{flex:1,padding:'8px 10px',borderRadius:10,border:'1px solid #4caf50',background: feedbackGiven==='up'?'#c8f7d0':'#e8fbe9',cursor:feedbackGiven==='up'?'default':'pointer'}}>👍 Bra</button>
              <button disabled={!advice||feedbackGiven==='down'} onClick={()=> { if(!advice||!lastGameState) return; mlFeedbackSystem.recordFeedback(lastGameState, advice.category ?? advice.reasoning, 'negative'); setFeedbackGiven('down'); }} style={{flex:1,padding:'8px 10px',borderRadius:10,border:'1px solid #e53935',background: feedbackGiven==='down'?'#ffd6d4':'#ffeceb',cursor:feedbackGiven==='down'?'default':'pointer'}}>👎 Dåligt</button>
            </div>
            
            <InfoText>
              Tips: AI-assistenten lär dig optimal strategi baserat på matematisk sannolikhet!
            </InfoText>
          </>
        )}
      </HelperContainer>
    </>
  );
};

export default StrategyHelper;
